import React from "react";
import { Play, Square, Activity, TrendingUp, AlertTriangle, CheckCircle, Clock, Brain } from "lucide-react";
import { useTradingControl } from "./useTradingControl";

const TradingControl: React.FC = () => {
  const {
    engineStatus,
    marketStatus,
    isStarting,
    isStopping,
    error,
    lastUpdate,
    fetchEngineStatus,
    startTrading,
    stopTrading,
  } = useTradingControl();

  if (!engineStatus) {
    return (
      <div key="loading" className="space-y-6">
        <div className="glass-card p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
            <span className="ml-3 text-slate-400">Loading trading status...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div key="loaded" className="space-y-6">
      <div className="glass-card p-6">
        <div className="flex flex-col gap-1 mb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <h2 className="text-xl font-semibold text-slate-100">Automated Trading Agent</h2>
          {lastUpdate && (
            <span className="text-xs text-slate-500">Last updated: {lastUpdate.toLocaleTimeString()}</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center">
              <Activity className="h-5 w-5 text-slate-400" />
              <span className="ml-2 text-sm font-medium text-slate-100">Engine Status</span>
            </div>
            <div className="mt-2 flex items-center">
              {engineStatus.isRunning ? (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  <span className="text-sm font-semibold text-green-300">Running</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-slate-500 rounded-full mr-2"></div>
                  <span className="text-sm font-semibold text-slate-400">Stopped</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 text-slate-400" />
              <span className="ml-2 text-sm font-medium text-slate-100">Active Models</span>
            </div>
            <div className="mt-2">
              <span className="text-2xl font-bold text-accent-300">{engineStatus.totalModels}</span>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-slate-400" />
              <span className="ml-2 text-sm font-medium text-slate-100">Market Status</span>
            </div>
            <div className="mt-2 flex items-center">
              {marketStatus ? (
                marketStatus.isOpen ? (
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                    <span className="text-sm font-semibold text-green-300">Open</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                    <span className="text-sm font-semibold text-red-300">Closed</span>
                  </div>
                )
              ) : (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-slate-500 rounded-full mr-2"></div>
                  <span className="text-sm font-semibold text-slate-400">Loading...</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center">
              <Brain className="h-5 w-5 text-slate-400" />
              <span className="ml-2 text-sm font-medium text-slate-100">Current Model</span>
            </div>
            <div className="mt-2">
              {engineStatus.appliedModels && engineStatus.appliedModels.length > 0 ? (
                <span className="text-sm font-semibold text-accent-300">{engineStatus.appliedModels[0].name}</span>
              ) : (
                <span className="text-sm text-slate-500">No models applied</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {!engineStatus.isRunning ? (
            <button
              onClick={startTrading}
              disabled={
                isStarting ||
                engineStatus.appliedModels?.length === 0 ||
                (Boolean(marketStatus) && !marketStatus?.isOpen)
              }
              className="flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isStarting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                  <span className="text-sm">Starting...</span>
                </>
              ) : (
                <>
                  <Play className="h-3 w-3 mr-2" />
                  <span className="text-sm">Start Trading</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={stopTrading}
              disabled={isStopping}
              className="flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isStopping ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                  <span className="text-sm">Stopping...</span>
                </>
              ) : (
                <>
                  <Square className="h-3 w-3 mr-2" />
                  <span className="text-sm">Stop Trading</span>
                </>
              )}
            </button>
          )}

          <button
            onClick={fetchEngineStatus}
            className="flex items-center px-3 py-2 bg-white/10 text-slate-300 text-sm font-medium rounded-lg hover:bg-white/15 transition-colors"
          >
            <Activity className="h-3 w-3 mr-2" />
            <span className="text-sm">Refresh Status</span>
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-red-300 mr-2" />
              <span className="text-sm text-red-300">{error}</span>
            </div>
          </div>
        )}

        {engineStatus.appliedModels?.length === 0 && (
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-yellow-300 mr-2" />
              <span className="text-sm text-yellow-200">
                No models applied. Please apply models from the EOS-Models page first.
              </span>
            </div>
          </div>
        )}

        {marketStatus && !marketStatus.isOpen && engineStatus.appliedModels?.length > 0 && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-red-300 mr-2" />
              <span className="text-sm text-red-300">
                Market is currently closed. Automated trading is only available during market hours.
              </span>
            </div>
          </div>
        )}
      </div>

      {engineStatus.activeModels && engineStatus.activeModels.length > 0 && (
        <div className="glass-card">
          <div className="px-6 py-4 border-b border-white/10">
            <h3 className="text-lg font-medium text-slate-100">Currently Active Models</h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {engineStatus.activeModels.map((modelName, index) => (
                <div key={index} className="flex items-center p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-300 mr-2" />
                  <span className="font-medium text-green-300">{modelName}</span>
                  <span className="ml-auto text-xs text-green-400">Executing trades...</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingControl;
